'use client';

import { ComponentRef, forwardRef, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { BadgeProps, Box, FormHelperText, TextField, Typography, TypographyProps, useTheme } from "@mui/material";
import { BoxProps } from "@mui/system";
import { SxProps, Theme } from "@mui/material/styles";

type EditableTypographyV2Props = Omit<TypographyProps, 'onChange'> & {
    label?: string;
    value: string | undefined;
    onChange: (value: string) => void;
    error?: boolean;
    helperText?: string;
    required?: boolean;
    commitMode?: 'change' | 'blur';
    autoSizeDuringEdit?: boolean;
    slots?: {
        root?: BoxProps;
        positioner?: BoxProps;
        badge?: BadgeProps;
    }
}

const EDITOR_INPUT_PADDING_X = 8;
const EDITOR_EDGE_GUTTER = 12;

const parseCssPixels = (value?: string): number => {
    if (!value) return 0;
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const EditableTypographyV2 = forwardRef<
    ComponentRef<typeof Typography>,
    EditableTypographyV2Props
>(
    ({
        value,
        onChange,
        error = false,
        required = false,
        commitMode = 'change',
        autoSizeDuringEdit = true,
        label,
        helperText,
        slots,
        ...rest
    }, ref) => {
        const [isEditing, setIsEditing] = useState(false);
        const [editorMinWidth, setEditorMinWidth] = useState<number>();
        const [editorMaxWidth, setEditorMaxWidth] = useState<number>();
        const [editorAutoWidth, setEditorAutoWidth] = useState<number>();
        const [editorValue, setEditorValue] = useState(value ?? "");
        const editorValueRef = useRef(value ?? "");
        const contentRef = useRef<HTMLDivElement | null>(null);
        const positionerRef = useRef<HTMLDivElement | null>(null);
        const measureRef = useRef<HTMLSpanElement | null>(null);
        const theme = useTheme();
        const resolvedValue = value ?? "";
        const isValueEmpty = resolvedValue.length === 0;
        const placeholderBaseText = label ?? "";
        const placeholderDisplayText = required && placeholderBaseText
            ? `${placeholderBaseText}*`
            : placeholderBaseText;
        const displayText = isValueEmpty ? placeholderDisplayText : resolvedValue;
        const typographyVariant = rest.variant ?? "body1";
        const resolvedTypographyStyles = useMemo(() => {
            const typographyVariantStyles =
                typeof typographyVariant === "string" && typographyVariant in theme.typography
                    ? theme.typography[typographyVariant as keyof typeof theme.typography]
                    : undefined;

            return typeof typographyVariantStyles === "object" ? typographyVariantStyles : {};
        }, [theme, typographyVariant]);
        const isEditorValueEmpty = editorValue.length === 0;
        const placeholderText = isEditorValueEmpty ? placeholderDisplayText : undefined;
        const measurementText = editorValue || placeholderText || " ";

        const startEditing = useCallback(() => {
            let width = contentRef.current?.getBoundingClientRect().width;
            if (!!width && width < 200) width = 200;

            setEditorMinWidth(width ? Math.ceil(width) : undefined);
            const nextValue = value ?? "";
            editorValueRef.current = nextValue;
            setEditorValue(nextValue);
            setIsEditing(true);
        }, [value]);

        useLayoutEffect(() => {
            if (!autoSizeDuringEdit || !isEditing || !measureRef.current) return;
            const measuredTextWidth = measureRef.current.getBoundingClientRect().width;
            const widthWithPadding = Math.ceil(measuredTextWidth + (EDITOR_INPUT_PADDING_X * 2));
            setEditorAutoWidth(widthWithPadding);
        }, [autoSizeDuringEdit, isEditing, measurementText, resolvedTypographyStyles]);

        useLayoutEffect(() => {
            if (!isEditing) return;

            const updateEditorMaxWidth = () => {
                const parentWidth = positionerRef.current?.parentElement?.getBoundingClientRect().width;
                if (!parentWidth) return;

                const contentStyles = contentRef.current ? window.getComputedStyle(contentRef.current) : null;
                const rootHorizontalPadding = contentStyles
                    ? parseCssPixels(contentStyles.paddingLeft) + parseCssPixels(contentStyles.paddingRight)
                    : 0;

                const nextMaxWidth = Math.floor(parentWidth - rootHorizontalPadding - (EDITOR_EDGE_GUTTER * 2));
                setEditorMaxWidth(nextMaxWidth > 0 ? nextMaxWidth : undefined);
            };

            updateEditorMaxWidth();

            const parentElement = positionerRef.current?.parentElement;
            if (!parentElement || typeof ResizeObserver === "undefined") return;

            const resizeObserver = new ResizeObserver(updateEditorMaxWidth);
            resizeObserver.observe(parentElement);

            return () => {
                resizeObserver.disconnect();
            };
        }, [isEditing]);

        const effectiveEditorWidth = Math.max(editorMinWidth ?? 0, editorAutoWidth ?? 0) || undefined;

        const textFieldBaseSx = {
            width: "100%",
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: 'breakout.main',
                transition: 'border-color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: 'breakout.dark',
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: 'primary.main',
                borderWidth: 1,
            },
            "& .MuiInputBase-root": {
                ...resolvedTypographyStyles,
                padding: '16.5px 14px',
                color: rest.color ?? "inherit",
            },
            "& .MuiInputBase-input": {
                ...resolvedTypographyStyles,
                padding: 0,
                color: rest.color ?? "inherit",
                textAlign: rest.align === "inherit" ? undefined : rest.align,
                "&::placeholder": {
                    color: error ? "error.main" : undefined,
                    opacity: error ? 1 : undefined,
                },
            },
            "& .MuiInputLabel-root": {
                color: 'breakout.main',
                transition: 'color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                "&.Mui-focused": {
                    color: 'primary.main',
                },
            },
        };
        const textFieldSx: SxProps<Theme> = Array.isArray(rest.sx)
            ? [textFieldBaseSx, ...rest.sx]
            : rest.sx
                ? [textFieldBaseSx, rest.sx]
                : textFieldBaseSx;

        return isEditing
            ? (
                <Box {...slots?.positioner} ref={positionerRef}>
                    <Box {...slots?.root} ref={contentRef}>
                        <Box
                            sx={{
                                position: "relative",
                                width: effectiveEditorWidth ? `${effectiveEditorWidth}px` : undefined,
                                minWidth: editorMinWidth ? `${editorMinWidth}px` : undefined,
                                maxWidth: editorMaxWidth ? `${editorMaxWidth}px` : undefined,
                            }}
                        >
                            <Box
                                component="span"
                                aria-hidden
                                ref={measureRef}
                                sx={{
                                    ...resolvedTypographyStyles,
                                    position: "absolute",
                                    visibility: "hidden",
                                    pointerEvents: "none",
                                    userSelect: "none",
                                    whiteSpace: "pre",
                                    inset: 0,
                                    height: 0,
                                    overflow: "hidden",
                                }}
                            >
                                {measurementText}
                            </Box>
                            <TextField
                                multiline
                                variant="outlined"
                                label={label}
                                placeholder={placeholderText}
                                onBlur={() => {
                                    if (commitMode === 'blur' && editorValueRef.current !== resolvedValue) {
                                        onChange(editorValueRef.current);
                                    }
                                    setIsEditing(false);
                                }}
                                autoFocus
                                value={editorValue}
                                onChange={(event) => {
                                    const nextValue = event.target.value;
                                    editorValueRef.current = nextValue;
                                    setEditorValue(nextValue);
                                    if (commitMode === 'change') onChange(nextValue);
                                }}
                                fullWidth
                                error={error}
                                helperText={helperText}
                                sx={textFieldSx}
                                required={required}
                            />
                        </Box>
                    </Box>
                </Box>
            )
            : (
                <Box {...slots?.positioner}>
                    <Box
                        {...slots?.root}
                        ref={contentRef}
                        onClick={startEditing}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                startEditing();
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label="Text bearbeiten"
                        sx={[
                            {
                                border: 1,
                                borderColor: 'breakout.main',
                                borderRadius: 1,
                                padding: '15.5px 13px',
                                cursor: 'pointer',
                                display: 'inline-block',
                                transition: 'border-color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    borderColor: 'breakout.light',
                                },
                                '&:focus-visible': {
                                    outline: 2,
                                    outlineStyle: 'solid',
                                    outlineColor: 'breakout.main',
                                    outlineOffset: 2,
                                },
                            },
                            ...(Array.isArray(slots?.root?.sx) ? slots.root.sx : slots?.root?.sx ? [slots.root.sx] : [])
                        ]}
                    >
                        <Typography
                            ref={ref}
                            sx={{
                                display: "inline-block",
                                width: "fit-content",
                                maxWidth: "100%",
                                ...resolvedTypographyStyles,
                                color: isValueEmpty
                                    ? (error ? "error.main" : "text.disabled")
                                    : (rest.color ?? "inherit"),
                            }}
                            {...rest}
                        >
                            {displayText}
                        </Typography>
                        {helperText && (
                            <FormHelperText
                                error={error}
                                sx={{ mt: 0.5, mx: 0 }}
                            >
                                {helperText}
                            </FormHelperText>
                        )}
                    </Box>
                </Box>
            )
    }
)

EditableTypographyV2.displayName = 'EditableTypographyV2';
export default EditableTypographyV2;
